// Search only changes the displayed collection. Callers retain their source
// records for financial totals, selections and mutations.
export function normalizeSearch(value) {
  return String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
}

function valuesOf(value) {
  if (Array.isArray(value)) return value.flatMap(valuesOf);
  if (value == null) return [];
  if (typeof value === 'number') return [String(value), value.toFixed(2), value.toLocaleString('en-US')];
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const date = new Date(`${text}T12:00:00`);
    if (!Number.isNaN(date.getTime())) return [text, date.toLocaleDateString('en-US'),
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })];
  }
  return [text];
}

// Adjacent transpositions are common typing mistakes (Adboe -> Adobe).
function editDistance(a, b) {
  const rows = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) rows[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) rows[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
      }
    }
  }
  return rows[a.length][b.length];
}

export function searchScore(values, query) {
  const needle = normalizeSearch(query);
  if (!needle) return 0;
  const fields = valuesOf(values).map(normalizeSearch).filter(Boolean);
  const compact = needle.replace(/ /g, '');
  if (fields.some(field => field === needle || field.replace(/ /g, '') === compact)) return 0;
  if (fields.some(field => field.startsWith(needle) || field.replace(/ /g, '').startsWith(compact))) return 1;
  if (fields.some(field => field.includes(needle) || field.replace(/ /g, '').includes(compact))) return 2;
  const words = fields.join(' ').split(' ');
  const allMatch = needle.split(' ').every(part => {
    if (words.some(word => word.includes(part))) return true;
    // Amounts, dates and very short fragments never use approximate matching.
    if (part.length < 4 || /\d/.test(part)) return false;
    const limit = part.length >= 8 ? 2 : 1;
    return words.some(word => Math.abs(word.length - part.length) <= limit && editDistance(part, word) <= limit);
  });
  return allMatch ? 3 : Infinity;
}

export function filterRecords(records, query, getValues, { rank = false } = {}) {
  if (!normalizeSearch(query)) return records;
  const matches = records.map((record, index) => ({ record, index, score: searchScore(getValues(record), query) }))
    .filter(item => Number.isFinite(item.score));
  if (rank) matches.sort((a, b) => a.score - b.score || a.index - b.index);
  return matches.map(item => item.record);
}
